// eslint-disable-next-line no-restricted-imports
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-tw';

import type { Locale } from 'lib/i18n/types';
import { MONTH, YEAR } from 'toolkit/utils/consts';
import { nbsp } from 'toolkit/utils/htmlEntities';

const DAYJS_LOCALE_MAP: Record<Locale, string> = {
  en: 'en',
  'zh-CN': 'zh-cn',
  'zh-TW': 'zh-tw',
};

const ABSOLUTE_TIME_FORMAT: Record<Locale, string> = {
  en: `MMM DD YYYY HH:mm:ss A (Z${ nbsp }UTC)`,
  'zh-CN': 'YYYY-MM-DD HH:mm:ss',
  'zh-TW': 'YYYY-MM-DD HH:mm:ss',
};

const SHORT_TIME_FORMAT: Record<Locale, string> = {
  en: 'MMM D, YYYY h:mm A',
  'zh-CN': 'YYYY-MM-DD HH:mm',
  'zh-TW': 'YYYY-MM-DD HH:mm',
};

const DATETIME_SECONDS_FORMAT: Record<Locale, string> = {
  en: 'DD MMM, HH:mm:ss',
  'zh-CN': 'YYYY-MM-DD HH:mm:ss',
  'zh-TW': 'YYYY-MM-DD HH:mm:ss',
};

dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(localizedFormat);
dayjs.extend(duration);
dayjs.extend(weekOfYear);
dayjs.extend(minMax);

dayjs.updateLocale('en', {
  formats: {
    llll: ABSOLUTE_TIME_FORMAT.en,
    lll: SHORT_TIME_FORMAT.en,
  },
  relativeTime: {
    s: '1s',
    ss: '%ds',
    future: 'in %s',
    past: '%s ago',
    m: '1m',
    mm: '%dm',
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    w: '1w',
    ww: '%dw',
    M: '1mo',
    MM: '%dmo',
    y: '1y',
    yy: '%dy',
  },
});

dayjs.updateLocale('zh-cn', {
  formats: {
    llll: ABSOLUTE_TIME_FORMAT['zh-CN'],
    lll: SHORT_TIME_FORMAT['zh-CN'],
  },
});

dayjs.updateLocale('zh-tw', {
  formats: {
    llll: ABSOLUTE_TIME_FORMAT['zh-TW'],
    lll: SHORT_TIME_FORMAT['zh-TW'],
  },
});

export function syncDayjsLocale(locale: Locale) {
  dayjs.locale(DAYJS_LOCALE_MAP[locale]);
}

export function formatAbsoluteTime(timestamp: string | number, locale: Locale) {
  return dayjs(timestamp).format(ABSOLUTE_TIME_FORMAT[locale]);
}

export function formatShortTime(timestamp: string | number, locale: Locale) {
  return dayjs(timestamp).format(SHORT_TIME_FORMAT[locale]);
}

export function formatDateTimeSeconds(timestamp: string | number, locale: Locale) {
  return dayjs(timestamp).format(DATETIME_SECONDS_FORMAT[locale]);
}

const CHART_AXIS_DAY_FORMAT: Record<Locale, string> = {
  en: 'D MMM',
  'zh-CN': 'M月D日',
  'zh-TW': 'M月D日',
};

const CHART_AXIS_MONTH_FORMAT: Record<Locale, string> = {
  en: 'MMM \'YY',
  'zh-CN': 'YYYY年M月',
  'zh-TW': 'YYYY年M月',
};

export function formatChartAxisDate(date: Date, spanMs: number, locale: Locale) {
  const formattedDate = dayjs(date);

  if (spanMs > 2 * YEAR) {
    return formattedDate.format('YYYY');
  }

  if (spanMs > 4 * MONTH) {
    return formattedDate.format(CHART_AXIS_MONTH_FORMAT[locale]);
  }

  return formattedDate.format(CHART_AXIS_DAY_FORMAT[locale]);
}

syncDayjsLocale('en');

export default dayjs;
